/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { FormControl, FormHelperText, InputLabel, OutlinedInput, Typography } from '@mui/material';

import { CountryCode, CountryCodes } from 'shared';

import { ArrowRoundedIcon } from '../../../components/svg-icons/ArrowRoundedIcon';
import { HUAutocomplete } from '../../../elements/HUAutocomplete/HUAutocomplete';
import { destructRenderInputParams } from '../../../elements/HUTextInput/utils';
import { Namespaces } from '../../../i18n';
import { useFormStyles } from '../../../styles/form';
import { useCreateOrganizationOverlayFormStyles } from '../CreateOrganizationOverlay.styles';
import {
  CreateOrganizationApiValidationErrors,
  CreateOrganizationValues,
  ResetApiErrorForField,
} from '../CreateOrganizationOverlay.types';

type CreateOrganizationStep2Props = FormikProps<CreateOrganizationValues> & {
  resetApiError: ResetApiErrorForField;
  apiErrors: CreateOrganizationApiValidationErrors;
  disabled?: boolean;
};

export function CreateOrganizationStep2(props: CreateOrganizationStep2Props) {
  const { touched, values, errors, handleChange, handleBlur, setFieldValue, resetApiError, apiErrors, disabled } =
    props;
  const formClasses = useFormStyles();
  const classes = useCreateOrganizationOverlayFormStyles();
  const { t } = useTranslation([Namespaces.CreateOrganization, Namespaces.Common]);

  const addressTouched = touched.address || {};
  const addressErrors = errors.address || {};

  const onChange = (name: keyof CreateOrganizationApiValidationErrors) => (event: ChangeEvent) => {
    handleChange(event);
    resetApiError(name);
  };

  const handleCountryChange = useCallback(
    async (_e: unknown, value: CountryCode | null) => {
      if (value) {
        await setFieldValue('address.countryCode', value, true);
        resetApiError('address.countryCode');
      }
    },
    [resetApiError, setFieldValue],
  );

  const getOptionLabel = useCallback((value: CountryCode) => t(`countries:${value}.name`), [t]);

  const line1Error = addressErrors.line1 || apiErrors['address.line1'];
  const hasLine1Error = addressTouched.line1 && !!line1Error;

  const line2Error = addressErrors.line2 || apiErrors['address.line2'];
  const hasLine2Error = addressTouched.line2 && !!line2Error;

  const line3Error = addressErrors.line3 || apiErrors['address.line3'];
  const hasLine3Error = addressTouched.line3 && !!line3Error;

  const postalCodeError = addressErrors.postalCode || apiErrors['address.postalCode'];
  const hasPostalCodeError = addressTouched.postalCode && !!postalCodeError;

  const cityError = addressErrors.city || apiErrors['address.city'];
  const hasCityError = addressTouched.city && !!cityError;

  const stateError = addressErrors.state || apiErrors['address.state'];
  const hasStateError = addressTouched.state && !!stateError;

  const countryCodeError = addressErrors.countryCode || apiErrors['address.countryCode'];
  const hasCountryCodeError = addressTouched.countryCode && !!countryCodeError;

  return (
    <>
      <Typography className={classes.subtitle} variant="h4" component="h4">
        {t('input_groups.location')}
      </Typography>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasLine1Error}
        variant="outlined"
      >
        <InputLabel htmlFor="address.line1">{t('input_labels.line1')}*</InputLabel>
        <OutlinedInput
          id="address.line1"
          name="address.line1"
          value={values.address.line1}
          onChange={onChange('address.line1')}
          label={t('input_labels.line1')}
          onBlur={handleBlur}
          data-cy="create-organization-form-address-line-1-input"
          disabled={disabled}
        />
        {hasLine1Error && (
          <FormHelperText data-cy="create-organization-form-address-line-1-error">
            {t(`validation.address.line1.${line1Error}`)}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasLine2Error}
        variant="outlined"
      >
        <InputLabel htmlFor="address.line2">{t('input_labels.line2')}</InputLabel>
        <OutlinedInput
          id="address.line2"
          name="address.line2"
          value={values.address.line2}
          onChange={onChange('address.line2')}
          label={t('input_labels.line2')}
          onBlur={handleBlur}
          disabled={disabled}
        />
        {hasLine2Error && <FormHelperText>{t(`validation.address.line2.${line2Error}`)}</FormHelperText>}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasLine3Error}
        variant="outlined"
      >
        <InputLabel htmlFor="address.line3">{t('input_labels.line3')}</InputLabel>
        <OutlinedInput
          id="address.line3"
          name="address.line3"
          value={values.address.line3}
          onChange={onChange('address.line3')}
          label={t('input_labels.line3')}
          onBlur={handleBlur}
          disabled={disabled}
        />
        {hasLine3Error && <FormHelperText>{t(`validation.address.line3.${line3Error}`)}</FormHelperText>}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasPostalCodeError}
        variant="outlined"
      >
        <InputLabel htmlFor="address.postalCode">{t('input_labels.postal_code')}*</InputLabel>
        <OutlinedInput
          id="address.postalCode"
          name="address.postalCode"
          value={values.address.postalCode}
          onChange={onChange('address.postalCode')}
          label={t('input_labels.postal_code')}
          onBlur={handleBlur}
          data-cy="create-organization-form-postal-code-input"
          disabled={disabled}
        />
        {hasPostalCodeError && (
          <FormHelperText data-cy="create-organization-form-postal-code-error">
            {t(`validation.address.postalCode.${postalCodeError}`)}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasCityError}
        variant="outlined"
      >
        <InputLabel htmlFor="address.city">{t('input_labels.city')}*</InputLabel>
        <OutlinedInput
          id="address.city"
          name="address.city"
          value={values.address.city}
          onChange={onChange('address.city')}
          label={t('input_labels.city')}
          onBlur={handleBlur}
          data-cy="create-organization-form-city-input"
          disabled={disabled}
        />
        {hasCityError && (
          <FormHelperText data-cy="create-organization-form-city-error">
            {t(`validation.address.city.${cityError}`)}
          </FormHelperText>
        )}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasStateError}
        variant="outlined"
      >
        <InputLabel htmlFor="address.state">{t('input_labels.state')}</InputLabel>
        <OutlinedInput
          id="address.state"
          name="address.state"
          value={values.address.state}
          onChange={onChange('address.state')}
          label={t('input_labels.state')}
          onBlur={handleBlur}
          disabled={disabled}
        />
        {hasStateError && <FormHelperText>{t(`validation.address.state.${stateError}`)}</FormHelperText>}
      </FormControl>
      <FormControl
        className={cx(formClasses.formControl, formClasses.widthMedium)}
        error={hasCountryCodeError}
        variant="outlined"
      >
        <InputLabel htmlFor="address.countryCode">{t('input_labels.country_code')}</InputLabel>
        <HUAutocomplete
          popupIcon={<ArrowRoundedIcon />}
          renderInput={renderParams => (
            <OutlinedInput
              {...destructRenderInputParams(renderParams)}
              name="address.countryCode"
              label={t('input_labels.country_code')}
              disabled={disabled}
              data-cy="create-organization-form-country-code-input"
            />
          )}
          options={[...CountryCodes]}
          disableClearable={true}
          value={values.address.countryCode}
          onChange={handleCountryChange}
          getOptionLabel={getOptionLabel}
          disabled={disabled}
        />
        {hasCountryCodeError && (
          <FormHelperText>{t(`validation.address.countryCode.${countryCodeError}`)}</FormHelperText>
        )}
      </FormControl>
    </>
  );
}
